/**
 * Food model. Grams are the base unit everywhere:
 * - every food stores nutrition per 100 g (liquids: per 100 ml, density ~1),
 * - every log entry stores the grams eaten,
 * - servings are just gram presets ("1 large egg" = 50 g).
 * Nutrients are an open map (see nutrients.ts); base keys are always present.
 */
import type { NutrientMap } from './nutrients';

export interface Serving {
  label: string;
  grams: number;
}

/** A weight stated in the label itself: "1 bar (55 g)", "100 g", "1 cup (240 ml)". */
const STATES_WEIGHT = /\b\d+(\.\d+)?\s?(g|ml)\b/i;

/** How a size reads: its label, with the weight added when the label doesn't say it ("1 large egg (50 g)"). */
export function sizeLabel(serving: Serving, liquid = false): string {
  return STATES_WEIGHT.test(serving.label) ? serving.label : `${serving.label} (${Math.round(serving.grams * 10) / 10} ${liquid ? 'ml' : 'g'})`;
}

export const FOOD_CATEGORIES = [
  'protein',
  'dairy',
  'grains',
  'fruit',
  'vegetables',
  'legumes',
  'nuts_seeds',
  'fats_oils',
  'snacks',
  'sweets',
  'beverages',
  'condiments',
  'prepared',
  'restaurant',
] as const;

export type FoodCategory = (typeof FOOD_CATEGORIES)[number];

export interface CatalogFood {
  /** Stable kebab-case slug, e.g. "egg-whole". Referenced from entries as "cat:<id>". */
  id: string;
  name: string;
  category: FoodCategory;
  /** Per 100 g. Always includes kcal, protein, carbs, fat; other keys only when known. */
  per100: NutrientMap;
  /** First serving is the default. */
  servings: Serving[];
  /** Extra search terms (plurals, common phrasings). */
  aliases?: string[];
  /** Synthetic demo barcode (EAN-13, "200" prefix) for a few packaged items only. */
  barcode?: string;
  liquid?: boolean;
}

/**
 * Compact constructor for catalog rows.
 * base = per-100 g [kcal, protein g, carbs g, fat g]; more = any other known nutrients per 100 g.
 */
export function f(
  id: string,
  name: string,
  category: FoodCategory,
  base: [kcal: number, protein: number, carbs: number, fat: number],
  more: NutrientMap,
  servings: Array<[label: string, grams: number]>,
  extra: { aliases?: string[]; barcode?: string; liquid?: boolean } = {},
): CatalogFood {
  const [kcal, protein, carbs, fat] = base;
  return {
    id,
    name,
    category,
    per100: { kcal, protein, carbs, fat, ...more },
    servings: servings.map(([label, grams]) => ({ label, grams })),
    ...extra,
  };
}

export const CATALOG_PREFIX = 'cat:';
export const CUSTOM_FOOD_PREFIX = 'food:';
